import React, { useState, useEffect, useRef } from 'react';
import { researchProjects, teachingActivities } from '../data/research';
import { ChevronDown, ChevronUp, Play, GraduationCap } from 'lucide-react';
import './Research.css';

function Research() {
  const [expanded, setExpanded] = useState(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08 }
    );
    cardsRef.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="research-page">
      <div className="container">
        <section className="page-hero">
          <h1>Research</h1>
          <p className="subtitle">
            Our group focuses on numerical general relativistic magnetohydrodynamics (GRMHD)
            to study the most energetic astrophysical processes, including black hole accretion, jet
            formation, gravitational wave signals, and heavy element nucleosynthesis.
          </p>
        </section>

        {/* Codes used */}
        <section className="section research-codes">
          <h2 className="section-title">Simulation Codes</h2>
          <div className="codes-grid">
            <div className="code-badge">
              <strong>HARM-MULTIEOS</strong>
              <span>Neutrino-cooled GRMHD with tabulated EOS</span>
            </div>
            <div className="code-badge">
              <strong>GLADIS</strong>
              <span>1D viscous accretion disk evolution, MPI-parallelized</span>
            </div>
          </div>
        </section>

        {/* Projects */}
        <section className="section">
          <h2 className="section-title">Active Projects</h2>
          <div className="projects-list">
            {researchProjects.map((project, i) => (
              <article
                className="project-card fade-in"
                key={i}
                ref={el => cardsRef.current[i] = el}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} loading="lazy" />
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  
                  {project.details && (
                    <>
                      <button
                        className="expand-btn"
                        onClick={() => setExpanded(expanded === i ? null : i)}
                      >
                        {expanded === i ? (
                          <><ChevronUp size={16} /> Less</>
                        ) : (
                          <><ChevronDown size={16} /> Read more</>
                        )}
                      </button>
                      {expanded === i && (
                        <div className="project-details">
                          <p>{project.details}</p>
                          {project.videoLink && (
                            <a href={project.videoLink} className="btn btn-ghost" target="_blank" rel="noopener noreferrer">
                              <Play size={16} /> Watch simulation
                            </a>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Teaching */}
        <section className="section research-teaching">
          <h2 className="section-title">
            <GraduationCap size={24} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Teaching & Internships
          </h2>
          <p style={{ marginBottom: '1.5rem' }}>
            Our group regularly hosts summer internship programs for students, providing hands-on
            experience with GRMHD simulations and scientific visualization.
          </p>
          <div className="teaching-timeline">
            {teachingActivities.map((activity, i) => (
              <div className="teaching-item" key={i}>
                <div className="teaching-dot" />
                <div className="teaching-content">
                  <h4>{activity.title}</h4>
                  <p>{activity.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Research;
